import { Alert, MenuItem, Stack, TextField } from "@mui/material";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import EditNoteRoundedIcon from "@mui/icons-material/EditNoteRounded";
import { DatePicker } from "@mui/x-date-pickers";
import dayjs, { Dayjs } from "dayjs";
import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { MobileNumberField } from "../../../components/common/form/MobileNumberField";
import { PublicFormButton, PublicFormCard } from "../../../components/common/form/PublicFormCard";
import { APIS } from "../../../modules/common/api/apis";
import { post } from "../../../modules/common/shared/AxiosAPI";
import { MESSAGES } from "../../../modules/common/shared/messages";
import { getStaticGrades } from "../../../modules/common/shared/Util";
import { getApiErrorMessage } from "../../../modules/common/shared/util/ApiErrorUtil";
import { isValidMobileNo } from "../../../modules/common/shared/util/FormValidationUtil";

const FIRST_GRADE = "PRE-KG";
const uppercaseInputSx = { "& input": { textTransform: "uppercase" } };

export const Enquiry = () => {
  const navigate = useNavigate();
  const location = useLocation();
  // Kept in router state (not component state) so a refresh after submitting shows the
  // confirmation again instead of a fresh form.
  const submitted = (location.state as { submitted?: boolean } | null)?.submitted === true;
  const [grades] = useState<string[]>(() => getStaticGrades());
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const [studentName, setStudentName] = useState("");
  const [dob, setDob] = useState<Dayjs | null>(null);
  const [gender, setGender] = useState("Male");
  const [grade, setGrade] = useState(FIRST_GRADE);
  const [currentSchool, setCurrentSchool] = useState("");
  const [guardianName, setGuardianName] = useState("");
  const [phoneNo, setPhoneNo] = useState<string | null>(null);

  const currentSchoolRequired = grade !== FIRST_GRADE;

  const submit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (isSubmitting) return;
    setErrorMessage(null);

    if (!dob || !dob.isValid() || dob.isAfter(dayjs())) {
      setErrorMessage("Please enter a valid date of birth.");
      return;
    }
    if (!phoneNo || !isValidMobileNo(phoneNo)) {
      setErrorMessage("Please enter a valid 10-digit mobile number.");
      return;
    }

    const enquiry = {
      name: studentName.trim().toUpperCase(),
      dob: dob.format("DD-MMM-YYYY"),
      gender: gender,
      grade: grade,
      currentSchool: currentSchoolRequired ? currentSchool.trim() : null,
      guardianName: guardianName.trim(),
      mobileNo: phoneNo,
      state: "INITIATED",
    };

    setIsSubmitting(true);
    post(APIS.PUBLIC_ENQUIRIES, null, enquiry)
      .then((res) => {
        if (res.status === 201) {
          navigate(location.pathname, { replace: true, state: { submitted: true } });
        } else {
          setErrorMessage("We couldn't submit your enquiry. Please try again.");
        }
      })
      .catch((err) => setErrorMessage(getApiErrorMessage(err)))
      .finally(() => setIsSubmitting(false));
  };

  if (submitted) {
    return (
      <PublicFormCard icon={<CheckCircleRoundedIcon />} tone="success" title="Enquiry submitted" subtitle={MESSAGES.ENQUIRY_SUBMIT_SUCCESFUL}>
        <PublicFormButton onClick={() => navigate("/")}>Back to home</PublicFormButton>
      </PublicFormCard>
    );
  }

  return (
    <PublicFormCard icon={<EditNoteRoundedIcon />} title="Admission Enquiry" subtitle={MESSAGES.STD_EQUIRY_FORM_HEADING} maxWidth={520}>
      <Stack component="form" spacing={2} onSubmit={submit}>
        {errorMessage && (
          <Alert severity="error" role="alert" onClose={() => setErrorMessage(null)}>
            {errorMessage}
          </Alert>
        )}

        <TextField
          fullWidth
          label="Name of the Student"
          name="sName"
          size="small"
          required
          autoFocus
          value={studentName}
          onChange={(e) => setStudentName(e.target.value)}
          sx={uppercaseInputSx}
        />

        <DatePicker
          label="Date of Birth"
          value={dob}
          onChange={setDob}
          format="DD-MMM-YYYY"
          maxDate={dayjs()}
          disableFuture
          slotProps={{ textField: { name: "dob", size: "small", required: true, fullWidth: true } }}
        />

        <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
          <TextField select fullWidth size="small" label="Gender" name="gender" value={gender} onChange={(e) => setGender(e.target.value)}>
            {["Male", "Female"].map((g) => (
              <MenuItem value={g} key={g}>
                {g}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            fullWidth
            size="small"
            label="Grade applying for"
            name="grade"
            value={grade}
            onChange={(e) => setGrade(e.target.value)}
          >
            {grades.map((g) => (
              <MenuItem value={g} key={g}>
                {g}
              </MenuItem>
            ))}
          </TextField>
        </Stack>

        {currentSchoolRequired && (
          <TextField
            fullWidth
            label="Name of the Current School"
            required
            name="currentSchool"
            size="small"
            value={currentSchool}
            onChange={(e) => setCurrentSchool(e.target.value)}
          />
        )}

        <TextField
          fullWidth
          label="Name of the Parent/Guardian"
          name="guardianName"
          size="small"
          required
          value={guardianName}
          onChange={(e) => setGuardianName(e.target.value)}
        />

        <MobileNumberField label="Mobile No" name="phone" value={phoneNo} onChange={setPhoneNo} required fullWidth />

        <PublicFormButton type="submit" loading={isSubmitting}>
          Submit enquiry
        </PublicFormButton>
      </Stack>
    </PublicFormCard>
  );
};
